/**
 * PRIVACY: Geen vrije tekst naar server; geaggregeerde sync via /_portal.
 * localStorage alleen in dev-cliëntmodus (jari-dig-* keys).
 */
import { useCallback, useState } from "react";
import {
  ToolLayout,
  ToolTabs,
  Onboarding,
  useAccess,
  hasCompletedOnboarding,
  setOnboardingComplete,
  ShareLinksManager,
  type OnboardingSlide,
} from "@jari-tools/shared";
import { DigShareProgress } from "./components/DigShareProgress";

const TOOL_SLUG = import.meta.env.VITE_TOOL_SLUG || "doe-iets-geks";
const TOOL_NAME = "Doe iets geks";
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL;

const ONBOARDING_SLIDES: OnboardingSlide[] = [
  {
    title: "Doe iets geks",
    body: "Deel een link met je client. Zij installeren de jari-app en krijgen elke dag een veilige opdracht om gedachten uit te dagen.",
  },
  {
    title: "Alleen in de app",
    body: "Clients gebruiken de jari-app op hun telefoon. Jij ziet hier geaggregeerde voortgang zodra ze syncen.",
  },
];

const SHARE_CONFIG_FIELDS = [
  {
    name: "ageGroup",
    label: "Leeftijdscategorie",
    type: "select" as const,
    required: true,
    options: [
      { value: "8-11", label: "8–11 jaar" },
      { value: "12-15", label: "12–15 jaar" },
      { value: "16-18", label: "16–18 jaar" },
    ],
  },
];

type Tab = "delen";

export function App() {
  const { access, name, loading, error, errorCode, isPaid, isGuest, toolId } = useAccess();
  const [tab, setTab] = useState<Tab>("delen");
  const [showOnboarding, setShowOnboarding] = useState(
    () => !hasCompletedOnboarding(TOOL_SLUG),
  );

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(TOOL_SLUG);
    setShowOnboarding(false);
  }, []);

  return (
    <>
      <ToolLayout
        toolName={TOOL_NAME}
        access={access}
        userName={name}
        loading={loading}
        error={error}
        errorCode={errorCode}
        portalUrl={PORTAL_URL}
        isGuest={isGuest}
        toolId={toolId}
        toolSlug={TOOL_SLUG}
        onShowOnboarding={() => setShowOnboarding(true)}
      >
        <ToolTabs
          tabs={[{ id: "delen", label: "Delen" }]}
          active={tab}
          onChange={(id) => setTab(id as Tab)}
        />
        {tab === "delen" ? (
          <ShareLinksManager
            toolSlug={TOOL_SLUG}
            access={access}
            configFields={SHARE_CONFIG_FIELDS}
            maxWeeks={52}
            expiryNotice="Links voor Doe iets geks blijven maximaal 52 weken geldig."
            introText="Maak een link voor de jari-app. Kies de leeftijdscategorie van je client."
            renderResult={(data) => <DigShareProgress data={data} />}
            renderStatus={(data) =>
              data && typeof data === "object" && "doneCount" in data
                ? `${(data as { doneCount: number }).doneCount}× gedaan`
                : null
            }
          />
        ) : null}
      </ToolLayout>

      <Onboarding
        open={showOnboarding}
        slides={ONBOARDING_SLIDES}
        onComplete={completeOnboarding}
        onSkip={completeOnboarding}
      />
    </>
  );
}
